import type { User } from "@supabase/supabase-js";
import { parseCampaignAttribution, type CampaignAttribution } from "@/lib/campaign-attribution";
import {
  sanitiseLocalLoginRedirect,
  sanitiseOptionalSignupNext,
  sanitiseSignupSource,
  type SignupEntryIntent,
} from "@/lib/signup-next";

export type GoogleAuthContext = {
  next?: string | null;
  intent?: SignupEntryIntent;
  role?: "employer";
  refCode?: string;
  invite?: string;
  /** First-party entry source, e.g. `guide:meribel` (see sanitiseSignupSource). */
  source?: string;
  campaignAttribution?: CampaignAttribution | null;
};

export function readGoogleAuthContext(params: Pick<URLSearchParams, "get">): GoogleAuthContext {
  const intent = params.get("intent");
  return {
    next: params.get("next") ?? params.get("redirect"),
    intent: intent === "work" || intent === "companion" ? intent : undefined,
    role: params.get("role") === "employer" ? "employer" : undefined,
    refCode: params.get("ref") ?? undefined,
    invite: params.get("invite") ?? undefined,
    source: sanitiseSignupSource(params.get("source")) ?? undefined,
    campaignAttribution: parseCampaignAttribution({
      utm_source: params.get("utm_source"), utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"), utm_content: params.get("utm_content"),
    }),
  };
}

export function buildGoogleCallbackUrl(origin: string, context: GoogleAuthContext): string {
  const url = new URL("/auth/callback", origin);
  url.searchParams.set("flow", "google");
  const next = sanitiseLocalLoginRedirect(context.next);
  if (next) url.searchParams.set("next", next);
  if (context.intent) url.searchParams.set("intent", context.intent);
  if (context.role) url.searchParams.set("role", context.role);
  if (context.refCode) url.searchParams.set("ref", context.refCode.slice(0, 120));
  if (context.invite) url.searchParams.set("invite", context.invite.slice(0, 120));
  const source = sanitiseSignupSource(context.source);
  if (source) url.searchParams.set("source", source);
  for (const [key, value] of Object.entries(parseCampaignAttribution(context.campaignAttribution ?? {}) ?? {})) {
    url.searchParams.set(key, value);
  }
  return url.toString();
}

/** Routing context only. Account access always comes from the verified session. */
export function googleSignupPath(params: URLSearchParams): string {
  if (params.get("role") === "employer") {
    const invite = params.get("invite");
    const result = new URLSearchParams();
    if (invite) result.set("invite", invite.slice(0, 120));
    const employerSource = sanitiseSignupSource(params.get("source"));
    if (employerSource) result.set("source", employerSource);
    const campaign = parseCampaignAttribution(Object.fromEntries(params));
    for (const [key, value] of Object.entries(campaign ?? {})) result.set(key, value);
    return `/employer-signup${result.size ? `?${result}` : ""}`;
  }
  const result = new URLSearchParams({ complete_profile: "true" });
  const next = sanitiseOptionalSignupNext(params.get("next"));
  if (next) result.set("next", next);
  const intent = params.get("intent");
  if (intent === "work" || intent === "companion") result.set("intent", intent);
  const ref = params.get("ref");
  if (ref) result.set("ref", ref.slice(0, 120));
  const source = sanitiseSignupSource(params.get("source"));
  if (source) result.set("source", source);
  const campaign = parseCampaignAttribution(Object.fromEntries(params));
  for (const [key, value] of Object.entries(campaign ?? {})) result.set(key, value);
  return `/signup?${result}`;
}

export function googleAuthErrorPath(next: string | null, context?: URLSearchParams): string {
  const params = new URLSearchParams({ error: "google_signin" });
  const safeNext = sanitiseLocalLoginRedirect(next);
  if (safeNext) params.set("redirect", safeNext);
  if (context) {
    const retry = new URL(buildGoogleCallbackUrl("https://peakwave.local", readGoogleAuthContext(context)));
    retry.searchParams.delete("flow");
    retry.searchParams.delete("next");
    for (const [key, value] of retry.searchParams) params.set(key, value);
  }
  return `/login?${params}`;
}

/** Provider metadata is a convenience for editable fields, never authorisation. */
export function googleProfileDefaults(user: User | null) {
  const metadata: Record<string, unknown> = user?.user_metadata ?? {};
  const text = (value: unknown) => typeof value === "string" ? value.trim().slice(0, 120) : "";
  const fullName = text(metadata.full_name) || text(metadata.name);
  const [first = "", ...rest] = fullName.split(/\s+/);
  return {
    email: user?.email ?? "",
    firstName: text(metadata.given_name) || first,
    lastName: text(metadata.family_name) || rest.join(" "),
  };
}
